import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, Upload, FileText, AlertTriangle, BarChart3, Download, Loader2,
  CheckCircle, X, Target, Shield, ChevronDown, ChevronRight, Plus,
  Trash2, RefreshCw, ArrowUp, ArrowDown, Minus, Info, Lightbulb, Flag,
  AlertCircle, Link2, Mail, GitBranch, Unlink
} from "lucide-react";
import ExportToolbar from "../components/ExportToolbar";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_CSV = `Activity_ID,WBS,Name,Planned_Start,Planned_Finish,Actual_Start,Actual_Finish,Duration_Days,Is_Critical,Percent_Complete
A001,1.0,Site Mobilization,2025-01-15,2025-02-15,2025-01-15,2025-02-20,31,Yes,100
A002,1.1,Site Clearance,2025-02-16,2025-03-15,2025-02-21,2025-03-25,27,Yes,100
A003,1.2,Temporary Works,2025-02-16,2025-03-30,2025-02-21,2025-04-10,42,No,100
A004,2.0,Foundation Piling,2025-03-16,2025-05-15,2025-03-26,2025-06-01,60,Yes,100
A005,2.1,Pile Cap Construction,2025-05-16,2025-06-30,2025-06-02,2025-07-20,45,Yes,100
A006,2.2,Ground Beams,2025-05-16,2025-06-15,2025-06-02,,30,No,60
A007,3.0,Structural Steel Erection,2025-07-01,2025-09-30,2025-07-21,,91,Yes,35
A008,3.1,Concrete Deck Pouring,2025-08-01,2025-10-31,,,91,Yes,0
A009,3.2,Waterproofing & Protection,2025-11-01,2025-11-30,,,30,No,0
A010,4.0,MEP Rough-In,2025-09-01,2025-12-15,,,105,No,0
A011,4.1,Electrical Distribution,2025-10-01,2025-12-31,,,91,No,0
A012,5.0,Finishing & Handover,2026-01-01,2026-03-31,,,90,Yes,0
A013,2.3,Dewatering Works,2025-03-16,2025-04-15,2025-03-26,2025-05-10,30,No,100
A014,3.3,Formwork Installation,2025-07-01,2025-08-15,2025-07-21,,45,Yes,20
A015,5.1,Testing & Commissioning,2026-02-01,2026-03-15,,,42,Yes,0`;

const EvidenceLinkingTab = ({ delayEvents, selectedProject, availableEvidence, evidenceLoading, fetchEvidence, linkEvidence, unlinkEvidence, getTypeBadge, getRespColor }) => {
  const [selectedDelay, setSelectedDelay] = useState(null);
  const [evidenceFilter, setEvidenceFilter] = useState("all");
  const linkedEvidence = availableEvidence.linked_evidence || [];
  const correspondences = availableEvidence.correspondences || [];
  const changeOrders = availableEvidence.change_orders || [];

  const getLinkedForDelay = (delayId) => linkedEvidence.filter(l => l.delay_id === delayId);
  const isLinked = (delayId, evidenceId) => linkedEvidence.some(l => l.delay_id === delayId && l.evidence_id === evidenceId);

  const filteredCorrespondences = evidenceFilter === "change_orders" ? [] : correspondences;
  const filteredChanges = evidenceFilter === "correspondences" ? [] : changeOrders;

  const corrTypeColors = { RFI: "bg-blue-100 text-blue-700", Notice: "bg-red-100 text-red-700", Instruction: "bg-amber-100 text-amber-700", Letter: "bg-zinc-100 text-zinc-700", Claim_Notice: "bg-red-100 text-red-700", Extension_of_Time: "bg-emerald-100 text-emerald-700", Variation_Order: "bg-purple-100 text-purple-700" };

  if (delayEvents.length === 0) {
    return (
      <div className="text-center py-12 border-2 border-dashed border-zinc-300 bg-zinc-50">
        <Link2 size={48} className="mx-auto mb-4 opacity-20" />
        <h3 className="font-bold text-zinc-600 mb-2">Run delay detection first</h3>
        <p className="text-sm text-zinc-400">Detect delays to link supporting evidence</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="evidence-linking-tab">
      <div className="border-l-4 border-emerald-600 bg-emerald-50 p-4">
        <p className="text-sm"><strong>Link correspondences and change orders</strong> to delay events as supporting evidence for claim quantification and report narratives.</p>
      </div>

      {correspondences.length === 0 && changeOrders.length === 0 && !evidenceLoading && (
        <div className="border-2 border-dashed border-zinc-300 bg-zinc-50 p-6 text-center">
          <Mail size={32} className="mx-auto mb-3 text-zinc-300" />
          <h4 className="font-bold text-zinc-600 mb-1">No correspondences or changes found</h4>
          <p className="text-xs text-zinc-400">Create correspondences in the Correspondences tab or change orders in Change Management to link them here.</p>
          <Button onClick={fetchEvidence} className="mt-3 bg-zinc-800 text-white text-xs font-bold uppercase" data-testid="btn-refresh-evidence">
            {evidenceLoading ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />} Refresh Evidence
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Delay Events List */}
        <div className="lg:col-span-1">
          <div className="border border-zinc-200 bg-white">
            <div className="bg-zinc-900 text-white p-3 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider">Delay Events ({delayEvents.length})</span>
            </div>
            <ScrollArea className="max-h-[500px]">
              <div className="divide-y divide-zinc-100">
                {delayEvents.map((ev) => {
                  const linked = getLinkedForDelay(ev.delay_id);
                  return (
                    <button key={ev.delay_id} onClick={() => setSelectedDelay(ev)} className={`w-full text-left p-3 hover:bg-zinc-50 transition-colors ${selectedDelay?.delay_id === ev.delay_id ? "bg-blue-50 border-l-4 border-[#EAB308]" : ""}`} data-testid={`evidence-delay-${ev.delay_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-black text-sm text-[#EAB308]">{ev.delay_id}</span>
                        <div className="flex items-center gap-1">
                          {linked.length > 0 && <span className="bg-emerald-100 text-emerald-700 text-[10px] font-bold px-1.5 py-0.5">{linked.length} linked</span>}
                          <span className={`px-1.5 py-0.5 text-[10px] font-bold border ${getRespColor(ev.responsibility)}`}>{ev.responsibility}</span>
                        </div>
                      </div>
                      <p className="text-xs font-medium mt-1 truncate">{ev.description}</p>
                      <p className="text-[10px] text-zinc-400">{ev.affected_activity_name} | {ev.impact_days}d impact</p>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Right: Evidence Pool + Linked Items */}
        <div className="lg:col-span-2 space-y-4">
          {selectedDelay ? (
            <>
              {/* Selected Delay Header */}
              <div className="border-2 border-[#EAB308] bg-blue-50 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-black text-lg text-[#EAB308]">{selectedDelay.delay_id}</span>
                    <span className="ml-2 text-sm font-medium">{selectedDelay.description}</span>
                  </div>
                  <span className="text-xs font-bold text-red-600 bg-red-50 px-2 py-1 border border-red-200">{selectedDelay.impact_days}d impact</span>
                </div>
              </div>

              {/* Already Linked Evidence */}
              {getLinkedForDelay(selectedDelay.delay_id).length > 0 && (
                <div className="border border-emerald-200 bg-emerald-50/50">
                  <div className="bg-emerald-700 text-white p-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Link2 size={12} /> Linked Evidence ({getLinkedForDelay(selectedDelay.delay_id).length})</div>
                  <div className="divide-y divide-emerald-200">
                    {getLinkedForDelay(selectedDelay.delay_id).map((le, i) => (
                      <div key={i} className="p-3 flex items-center justify-between" data-testid={`linked-ev-${i}`}>
                        <div className="flex items-center gap-3">
                          {le.evidence_type === "correspondence" ? <Mail size={16} className="text-emerald-600" /> : <GitBranch size={16} className="text-purple-600" />}
                          <div>
                            <p className="text-xs font-bold">{le.evidence_ref}</p>
                            <p className="text-[10px] text-zinc-500">{le.evidence_type === "correspondence" ? "Correspondence" : "Change Order"} — {le.evidence_summary}</p>
                          </div>
                        </div>
                        <button onClick={() => unlinkEvidence(selectedDelay.delay_id, le.evidence_id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`unlink-${i}`}><Unlink size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Filter + Available Evidence */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold uppercase text-zinc-500">Available Evidence:</span>
                <div className="flex gap-1">
                  {[{k:"all",l:"All"},{k:"correspondences",l:"Correspondences"},{k:"change_orders",l:"Change Orders"}].map(f => (
                    <button key={f.k} onClick={() => setEvidenceFilter(f.k)} className={`px-2 py-1 text-[10px] font-bold uppercase border transition-colors ${evidenceFilter === f.k ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"}`}>{f.l}</button>
                  ))}
                </div>
                <Button onClick={fetchEvidence} size="sm" variant="outline" className="ml-auto h-7 text-xs" data-testid="btn-refresh-evidence2">
                  {evidenceLoading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                </Button>
              </div>

              {/* Correspondences */}
              {filteredCorrespondences.length > 0 && (
                <div className="border border-zinc-200 bg-white">
                  <div className="bg-zinc-800 text-white p-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Mail size={12} /> Correspondences ({filteredCorrespondences.length})</div>
                  <ScrollArea className="max-h-[200px]">
                    <div className="divide-y divide-zinc-100">
                      {filteredCorrespondences.map((c) => {
                        const linked = isLinked(selectedDelay.delay_id, c.id);
                        return (
                          <div key={c.id} className={`p-3 flex items-center justify-between hover:bg-zinc-50 ${linked ? "bg-emerald-50" : ""}`}>
                            <div className="flex-1 min-w-0 mr-3">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-bold text-zinc-500">{c.tracking_id}</span>
                                <span className={`px-1.5 py-0.5 text-[10px] font-bold ${corrTypeColors[c.type] || "bg-zinc-100 text-zinc-700"}`}>{c.type?.replace("_", " ")}</span>
                                <span className={`px-1.5 py-0.5 text-[10px] font-bold ${c.priority === "critical" ? "bg-red-100 text-red-700" : c.priority === "high" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600"}`}>{c.priority}</span>
                              </div>
                              <p className="text-xs font-medium truncate mt-0.5">{c.subject}</p>
                              <p className="text-[10px] text-zinc-400">{c.from_party} → {c.to_party} | {c.status}</p>
                            </div>
                            {linked ? (
                              <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100 px-2 py-1 flex-shrink-0"><CheckCircle size={10} className="inline mr-1" />Linked</span>
                            ) : (
                              <Button size="sm" onClick={() => linkEvidence(selectedDelay.delay_id, "correspondence", c)} className="h-7 text-[10px] font-bold bg-[#EAB308] text-white flex-shrink-0" data-testid={`link-corr-${c.id}`}>
                                <Link2 size={10} className="mr-1" /> Link
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Change Orders */}
              {filteredChanges.length > 0 && (
                <div className="border border-zinc-200 bg-white">
                  <div className="bg-purple-800 text-white p-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2"><GitBranch size={12} /> Change Orders ({filteredChanges.length})</div>
                  <ScrollArea className="max-h-[200px]">
                    <div className="divide-y divide-zinc-100">
                      {filteredChanges.map((co) => {
                        const linked = isLinked(selectedDelay.delay_id, co.id);
                        return (
                          <div key={co.id} className={`p-3 flex items-center justify-between hover:bg-zinc-50 ${linked ? "bg-purple-50" : ""}`}>
                            <div className="flex-1 min-w-0 mr-3">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-bold text-purple-700">{co.change_number}</span>
                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700">{co.change_type}</span>
                                <span className={`px-1.5 py-0.5 text-[10px] font-bold ${co.status === "approved" ? "bg-emerald-100 text-emerald-700" : co.status === "rejected" ? "bg-red-100 text-red-700" : "bg-zinc-100 text-zinc-600"}`}>{co.status}</span>
                              </div>
                              <p className="text-xs font-medium truncate mt-0.5">{co.title}</p>
                              <p className="text-[10px] text-zinc-400">Cost: ${(co.estimated_cost_impact || 0).toLocaleString()} | Time: {co.estimated_time_impact_days || 0}d</p>
                            </div>
                            {linked ? (
                              <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-2 py-1 flex-shrink-0"><CheckCircle size={10} className="inline mr-1" />Linked</span>
                            ) : (
                              <Button size="sm" onClick={() => linkEvidence(selectedDelay.delay_id, "change_order", co)} className="h-7 text-[10px] font-bold bg-purple-700 text-white flex-shrink-0" data-testid={`link-co-${co.id}`}>
                                <Link2 size={10} className="mr-1" /> Link
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {filteredCorrespondences.length === 0 && filteredChanges.length === 0 && !evidenceLoading && (
                <div className="text-center py-8 border-2 border-dashed border-zinc-300 bg-zinc-50">
                  <p className="text-sm text-zinc-400">No evidence items available for the current filter.</p>
                  <p className="text-xs text-zinc-400 mt-1">Create correspondences or change orders in their respective tabs first.</p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 border-2 border-dashed border-zinc-300 bg-zinc-50">
              <Link2 size={48} className="mx-auto mb-4 opacity-20" />
              <h3 className="font-bold text-zinc-600 mb-2">Select a Delay Event</h3>
              <p className="text-sm text-zinc-400">Click on a delay event from the left panel to link correspondences and change orders as supporting evidence</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DelayAnalysisView = ({ contracts }) => {
  const [activeTab, setActiveTab] = useState("upload");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stepLoading, setStepLoading] = useState("");

  // Evidence linking state
  const [availableEvidence, setAvailableEvidence] = useState({ correspondences: [], change_orders: [], linked_evidence: [] });
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  // Create project form
  const [newProject, setNewProject] = useState({
    project_name: "", framework: "FIDIC", sector: "", contract_id: "",
    contract_value: "", duration_months: "", daily_prolongation_rate: "",
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/delay-analysis/projects`);
      setProjects(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  useEffect(() => {
    if (activeTab === "evidence" && selectedProject && delayEvents.length > 0) {
      fetchEvidence();
    }
  }, [activeTab, selectedProject?.id]);

  const createProject = async () => {
    if (!newProject.project_name) { toast.error("Enter project name"); return; }
    setLoading(true);
    try {
      const payload = {
        ...newProject,
        contract_value: newProject.contract_value ? parseFloat(newProject.contract_value) : null,
        duration_months: newProject.duration_months ? parseInt(newProject.duration_months) : null,
        daily_prolongation_rate: newProject.daily_prolongation_rate ? parseFloat(newProject.daily_prolongation_rate) : null,
      };
      const res = await axios.post(`${API}/delay-analysis/projects`, payload);
      setSelectedProject(res.data);
      setProjects(prev => [res.data, ...prev]);
      toast.success("Delay analysis project created");
      setNewProject({ project_name: "", framework: "FIDIC", sector: "", contract_id: "", contract_value: "", duration_months: "", daily_prolongation_rate: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create project"); }
    finally { setLoading(false); }
  };

  const selectProject = async (projectId) => {
    try {
      const res = await axios.get(`${API}/delay-analysis/projects/${projectId}`);
      setSelectedProject(res.data);
    } catch { toast.error("Failed to load project"); }
  };

  const uploadSchedule = async (file) => {
    if (!selectedProject) return;
    setStepLoading("upload");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/upload-schedule`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Parsed ${res.data.activity_count} activities`);
      await selectProject(selectedProject.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setStepLoading(""); }
  };

  const uploadProgress = async (file) => {
    if (!selectedProject) return;
    setStepLoading("progress");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/upload-progress`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Updated ${res.data.updated_count} activities`);
      await selectProject(selectedProject.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setStepLoading(""); }
  };

  const runComparison = async () => {
    if (!selectedProject) return;
    setStepLoading("compare");
    try {
      await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/compare`);
      toast.success("Schedule comparison complete");
      await selectProject(selectedProject.id);
      setActiveTab("comparison");
    } catch (err) { toast.error(err.response?.data?.detail || "Comparison failed"); }
    finally { setStepLoading(""); }
  };

  const detectDelays = async () => {
    if (!selectedProject) return;
    setStepLoading("detect");
    try {
      await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/detect-delays`, {}, { timeout: 120000 });
      toast.success("Delay detection & classification complete");
      await selectProject(selectedProject.id);
      setActiveTab("events");
    } catch (err) { toast.error(err.response?.data?.detail || "Detection failed"); }
    finally { setStepLoading(""); }
  };

  const runQuantification = async () => {
    if (!selectedProject) return;
    setStepLoading("quantify");
    try {
      const rate = selectedProject.daily_prolongation_rate || 0;
      await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/quantify`, { daily_prolongation_rate: rate });
      toast.success("Quantification complete");
      await selectProject(selectedProject.id);
      setActiveTab("quantification");
    } catch (err) { toast.error(err.response?.data?.detail || "Quantification failed"); }
    finally { setStepLoading(""); }
  };

  const fetchEvidence = async () => {
    if (!selectedProject) return;
    setEvidenceLoading(true);
    try {
      const res = await axios.get(`${API}/delay-analysis/projects/${selectedProject.id}/evidence`);
      setAvailableEvidence(res.data);
    } catch { toast.error("Failed to fetch evidence"); }
    finally { setEvidenceLoading(false); }
  };

  const linkEvidence = async (delayId, evidenceType, item) => {
    if (!selectedProject) return;
    const existing = availableEvidence.linked_evidence || [];
    const ref = evidenceType === "correspondence" ? (item.tracking_id || item.subject) : (item.change_number || item.title);
    const summary = evidenceType === "correspondence" ? item.subject : item.title;

    // Check for duplicates
    if (existing.some(l => l.delay_id === delayId && l.evidence_id === item.id)) {
      toast.error("Already linked");
      return;
    }

    const newLinks = [...existing, {
      delay_id: delayId,
      evidence_type: evidenceType,
      evidence_id: item.id,
      evidence_ref: ref,
      evidence_summary: summary,
    }];

    try {
      const res = await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/link-evidence`, { links: newLinks });
      setAvailableEvidence(prev => ({ ...prev, linked_evidence: res.data.linked_evidence }));
      toast.success(`Linked to ${delayId}`);
      await selectProject(selectedProject.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Link failed"); }
  };

  const unlinkEvidence = async (delayId, evidenceId) => {
    if (!selectedProject) return;
    try {
      const res = await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/unlink-evidence`, { delay_id: delayId, evidence_id: evidenceId });
      setAvailableEvidence(prev => ({ ...prev, linked_evidence: res.data.linked_evidence }));
      toast.success("Evidence unlinked");
      await selectProject(selectedProject.id);
    } catch { toast.error("Unlink failed"); }
  };

  const generateReport = async () => {
    if (!selectedProject) return;
    setStepLoading("report");
    try {
      const res = await axios.post(`${API}/delay-analysis/projects/${selectedProject.id}/generate-report`, {}, { responseType: "blob", timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `delay_claims_report_${selectedProject.id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report downloaded");
    } catch (err) { toast.error("Report generation failed"); }
    finally { setStepLoading(""); }
  };

  const downloadSampleCSV = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_schedule.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const deleteProject = async (pid) => {
    try {
      await axios.delete(`${API}/delay-analysis/projects/${pid}`);
      setProjects(prev => prev.filter(p => p.id !== pid));
      if (selectedProject?.id === pid) setSelectedProject(null);
      toast.success("Project deleted");
    } catch { toast.error("Delete failed"); }
  };

  const activities = selectedProject?.activities || [];
  const comparison = selectedProject?.comparison || {};
  const delayEvents = selectedProject?.delay_events || [];
  const delaySummary = selectedProject?.delay_summary || {};
  const quantification = selectedProject?.quantification || {};
  const compSummary = comparison?.summary || {};

  const getVarIcon = (v) => v > 0 ? <ArrowUp size={12} className="text-red-500" /> : v < 0 ? <ArrowDown size={12} className="text-emerald-500" /> : <Minus size={12} className="text-zinc-400" />;
  const getStatusBadge = (s) => s === "delayed" ? "bg-red-100 text-red-700 border-red-200" : s === "ahead" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-zinc-100 text-zinc-600 border-zinc-200";
  const getTypeBadge = (t) => t === "Excusable" ? "bg-blue-100 text-blue-700" : "bg-zinc-200 text-zinc-700";
  const getCompBadge = (c) => c === "Compensable" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600";
  const getRespColor = (r) => r === "Employer" ? "text-red-600 bg-red-50 border-red-200" : r === "Contractor" ? "text-blue-600 bg-blue-50 border-blue-200" : "text-amber-600 bg-amber-50 border-amber-200";

  return (
    <div className="space-y-6" data-testid="delay-analysis-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo'] mb-2">Delay Analysis</h1>
          <p className="text-zinc-500">Forensic delay analysis with schedule comparison, classification, causation & claims reporting</p>
        </div>
        <ExportToolbar title="Delay Analysis" pdfFilename="delay-analysis" />
      </div>

      {/* Project Selector / Creator */}
      <div className="border-2 border-black brutal-shadow bg-white p-5" data-testid="project-selector">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Create New Project */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">New Analysis Project</h3>
            <div className="space-y-3">
              <input value={newProject.project_name} onChange={e => setNewProject(p => ({ ...p, project_name: e.target.value }))} placeholder="Project Name *" className="w-full border-2 border-zinc-200 p-2.5 text-sm focus:border-black focus:outline-none" data-testid="delay-project-name" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={newProject.framework} onValueChange={v => setNewProject(p => ({ ...p, framework: v }))}>
                  <SelectTrigger className="border-2 border-zinc-200" data-testid="delay-framework"><SelectValue placeholder="Framework" /></SelectTrigger>
                  <SelectContent>
                    {["FIDIC", "NEC", "JCT", "AIA", "ICC", "GCC", "AS4000"].map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={newProject.sector} onValueChange={v => setNewProject(p => ({ ...p, sector: v }))}>
                  <SelectTrigger className="border-2 border-zinc-200"><SelectValue placeholder="Sector" /></SelectTrigger>
                  <SelectContent>
                    {["Rail", "Energy", "Infrastructure", "Buildings", "Roads", "Water", "Oil & Gas", "Mining"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input type="text" value={newProject.contract_value} onChange={e => setNewProject(p => ({ ...p, contract_value: e.target.value }))} placeholder="Contract Value ($)" className="w-full border-2 border-zinc-200 p-2.5 text-sm focus:border-black focus:outline-none" />
                <input type="text" value={newProject.duration_months} onChange={e => setNewProject(p => ({ ...p, duration_months: e.target.value }))} placeholder="Duration (months)" className="w-full border-2 border-zinc-200 p-2.5 text-sm focus:border-black focus:outline-none" />
                <input type="text" value={newProject.daily_prolongation_rate} onChange={e => setNewProject(p => ({ ...p, daily_prolongation_rate: e.target.value }))} placeholder="Daily Rate ($)" className="w-full border-2 border-zinc-200 p-2.5 text-sm focus:border-black focus:outline-none" data-testid="delay-daily-rate" />
              </div>
              {contracts?.length > 0 && (
                <select value={newProject.contract_id} onChange={e => setNewProject(p => ({ ...p, contract_id: e.target.value }))} className="w-full border-2 border-zinc-200 p-2.5 text-sm focus:outline-none" data-testid="delay-contract-select">
                  <option value="">Link to existing contract (optional)</option>
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.project_name} ({c.framework})</option>)}
                </select>
              )}
              <Button onClick={createProject} disabled={loading || !newProject.project_name} className="w-full bg-black text-white h-10 font-bold uppercase tracking-wider text-xs" data-testid="btn-create-project">
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />} Create Analysis Project
              </Button>
            </div>
          </div>

          {/* Existing Projects */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">Existing Projects ({projects.length})</h3>
            <ScrollArea className="max-h-[280px]">
              <div className="space-y-2">
                {projects.length === 0 && <p className="text-sm text-zinc-400 text-center py-6">No analysis projects yet</p>}
                {projects.map(p => (
                  <div key={p.id} onClick={() => selectProject(p.id)} className={`p-3 border-2 cursor-pointer transition-all hover:border-black ${selectedProject?.id === p.id ? "border-[#EAB308] bg-blue-50" : "border-zinc-200 bg-white"}`} data-testid={`project-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm">{p.project_name}</p>
                        <p className="text-xs text-zinc-500">{p.framework} | {p.sector || "N/A"} | {(p.activities || []).length} activities</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {p.baseline_uploaded && <div className="w-2 h-2 rounded-full bg-emerald-500" title="Schedule uploaded" />}
                          {p.delay_events?.length > 0 && <div className="w-2 h-2 rounded-full bg-amber-500" title="Delays detected" />}
                          {p.quantification && <div className="w-2 h-2 rounded-full bg-blue-500" title="Quantified" />}
                        </div>
                        <button onClick={e => { e.stopPropagation(); deleteProject(p.id); }} className="text-zinc-400 hover:text-red-500"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>

      {/* Analysis Workflow - only show when project selected */}
      {selectedProject && (
        <>
          {/* Workflow Progress */}
          <div className="border-2 border-zinc-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500">Analysis Workflow</h3>
              <span className="text-xs text-zinc-400">— {selectedProject.project_name}</span>
            </div>
            <div className="grid grid-cols-7 gap-1">
              {[
                { step: 1, label: "Upload", done: selectedProject.baseline_uploaded, key: "upload" },
                { step: 2, label: "Compare", done: !!selectedProject.comparison, key: "comparison" },
                { step: 3, label: "Detect", done: delayEvents.length > 0, key: "events" },
                { step: 4, label: "Evidence", done: (selectedProject.linked_evidence || []).length > 0, key: "evidence" },
                { step: 5, label: "Clauses", done: delayEvents.some(e => e.clause_reference), key: "clauses" },
                { step: 6, label: "Quantify", done: !!selectedProject.quantification, key: "quantification" },
                { step: 7, label: "Report", done: false, key: "report" },
              ].map(s => (
                <button key={s.step} onClick={() => setActiveTab(s.key)} className={`p-2 text-center border-2 transition-all ${activeTab === s.key ? "border-black bg-zinc-900 text-white" : s.done ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-white text-zinc-400"}`} data-testid={`step-${s.key}`}>
                  <div className="text-lg font-black">{s.done ? <CheckCircle size={18} className="mx-auto" /> : s.step}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider mt-0.5">{s.label}</div>
                </button>
              ))}
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-zinc-100 p-1 flex-wrap h-auto">
              <TabsTrigger value="upload"><Upload size={14} className="mr-1" /> Data Upload</TabsTrigger>
              <TabsTrigger value="comparison"><BarChart3 size={14} className="mr-1" /> Schedule Comparison</TabsTrigger>
              <TabsTrigger value="events"><AlertTriangle size={14} className="mr-1" /> Delay Events</TabsTrigger>
              <TabsTrigger value="evidence" onClick={() => { if (selectedProject && delayEvents.length > 0) fetchEvidence(); }}><Link2 size={14} className="mr-1" /> Evidence Linking</TabsTrigger>
              <TabsTrigger value="clauses"><FileText size={14} className="mr-1" /> Clause Linking</TabsTrigger>
              <TabsTrigger value="quantification"><Target size={14} className="mr-1" /> Quantification</TabsTrigger>
              <TabsTrigger value="report"><Download size={14} className="mr-1" /> Claims Report</TabsTrigger>
            </TabsList>

            {/* TAB 1: Upload */}
            <TabsContent value="upload">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Baseline Schedule Upload */}
                  <div className="border-2 border-zinc-200 bg-white p-5">
                    <h3 className="font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Upload size={16} className="text-[#EAB308]" /> Baseline Schedule (CSV)
                    </h3>
                    <p className="text-xs text-zinc-500 mb-4">Upload your project schedule with planned dates, durations, and critical path information.</p>
                    <div className="border-2 border-dashed border-zinc-300 p-6 text-center hover:border-[#EAB308] transition-colors">
                      <input type="file" accept=".csv" onChange={e => e.target.files[0] && uploadSchedule(e.target.files[0])} className="hidden" id="schedule-upload" disabled={!!stepLoading} />
                      <label htmlFor="schedule-upload" className="cursor-pointer" data-testid="schedule-upload-label">
                        {stepLoading === "upload" ? <Loader2 size={32} className="mx-auto mb-2 animate-spin text-[#EAB308]" /> : <Upload size={32} className="mx-auto mb-2 text-zinc-400" />}
                        <p className="text-sm font-bold">{stepLoading === "upload" ? "Parsing schedule..." : "Choose CSV file"}</p>
                        <p className="text-xs text-zinc-400 mt-1">Supports Primavera P6, MS Project CSV exports</p>
                      </label>
                    </div>
                    <button onClick={downloadSampleCSV} className="mt-3 text-xs text-[#EAB308] underline flex items-center gap-1" data-testid="btn-download-sample"><Download size={12} /> Download sample CSV template</button>
                    {selectedProject.baseline_uploaded && (
                      <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                        <CheckCircle size={14} className="text-emerald-600" />
                        <span className="text-xs font-bold text-emerald-700">{activities.length} activities loaded</span>
                      </div>
                    )}
                  </div>

                  {/* Progress Data Upload */}
                  <div className="border-2 border-zinc-200 bg-white p-5">
                    <h3 className="font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                      <RefreshCw size={16} className="text-[#E11D48]" /> Progress Update (CSV)
                    </h3>
                    <p className="text-xs text-zinc-500 mb-4">Upload as-built data with actual dates and progress percentages to update the baseline.</p>
                    <div className={`border-2 border-dashed p-6 text-center transition-colors ${selectedProject.baseline_uploaded ? "border-zinc-300 hover:border-[#E11D48]" : "border-zinc-200 bg-zinc-50"}`}>
                      <input type="file" accept=".csv" onChange={e => e.target.files[0] && uploadProgress(e.target.files[0])} className="hidden" id="progress-upload" disabled={!!stepLoading || !selectedProject.baseline_uploaded} />
                      <label htmlFor="progress-upload" className={selectedProject.baseline_uploaded ? "cursor-pointer" : "cursor-not-allowed"} data-testid="progress-upload-label">
                        {stepLoading === "progress" ? <Loader2 size={32} className="mx-auto mb-2 animate-spin text-[#E11D48]" /> : <RefreshCw size={32} className="mx-auto mb-2 text-zinc-400" />}
                        <p className="text-sm font-bold">{!selectedProject.baseline_uploaded ? "Upload baseline first" : stepLoading === "progress" ? "Updating..." : "Choose progress CSV"}</p>
                      </label>
                    </div>
                    {selectedProject.progress_uploaded && (
                      <div className="mt-3 p-2 bg-emerald-50 border border-emerald-200 flex items-center gap-2">
                        <CheckCircle size={14} className="text-emerald-600" />
                        <span className="text-xs font-bold text-emerald-700">Progress data merged</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {selectedProject.baseline_uploaded && (
                  <div className="flex gap-3">
                    <Button onClick={runComparison} disabled={!!stepLoading} className="bg-[#EAB308] text-white font-bold uppercase tracking-wider text-xs" data-testid="btn-compare-schedule">
                      {stepLoading === "compare" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BarChart3 size={14} className="mr-2" />} Run Schedule Comparison
                    </Button>
                    <Button onClick={detectDelays} disabled={!!stepLoading} className="bg-[#E11D48] text-white font-bold uppercase tracking-wider text-xs" data-testid="btn-detect-delays">
                      {stepLoading === "detect" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <AlertTriangle size={14} className="mr-2" />} Detect & Classify Delays (AI)
                    </Button>
                  </div>
                )}

                {/* Activities Preview */}
                {activities.length > 0 && (
                  <div className="border border-zinc-200 bg-white">
                    <div className="bg-zinc-900 text-white p-3 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wider">Schedule Activities ({activities.length})</span>
                    </div>
                    <ScrollArea className="max-h-[350px]">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-100 sticky top-0">
                          <tr>
                            <th className="p-2 text-left font-bold uppercase">ID</th>
                            <th className="p-2 text-left font-bold uppercase">Activity</th>
                            <th className="p-2 text-center font-bold uppercase">Planned Start</th>
                            <th className="p-2 text-center font-bold uppercase">Planned Finish</th>
                            <th className="p-2 text-center font-bold uppercase">Actual Start</th>
                            <th className="p-2 text-center font-bold uppercase">Actual Finish</th>
                            <th className="p-2 text-center font-bold uppercase">Critical</th>
                            <th className="p-2 text-center font-bold uppercase">Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activities.map((a, i) => (
                            <tr key={i} className={`border-b border-zinc-100 hover:bg-zinc-50 ${a.is_critical ? "bg-red-50/30" : ""}`}>
                              <td className="p-2 font-mono text-zinc-500">{a.activity_id}</td>
                              <td className="p-2 font-medium">{a.name}</td>
                              <td className="p-2 text-center">{a.planned_start || "-"}</td>
                              <td className="p-2 text-center">{a.planned_finish || "-"}</td>
                              <td className="p-2 text-center">{a.actual_start || "-"}</td>
                              <td className="p-2 text-center">{a.actual_finish || "-"}</td>
                              <td className="p-2 text-center">{a.is_critical ? <Flag size={12} className="inline text-red-500" /> : "-"}</td>
                              <td className="p-2 text-center">{a.percent_complete || 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* TAB 2: Schedule Comparison */}
            <TabsContent value="comparison">
              <div className="space-y-4">
                {!selectedProject.comparison ? (
                  <div className="text-center py-12 border-2 border-dashed border-zinc-300 bg-zinc-50">
                    <BarChart3 size={48} className="mx-auto mb-4 opacity-20" />
                    <h3 className="font-bold text-zinc-600 mb-2">No comparison data yet</h3>
                    <p className="text-sm text-zinc-400 mb-4">Upload a schedule and click "Run Schedule Comparison"</p>
                    <Button onClick={runComparison} disabled={!selectedProject.baseline_uploaded || !!stepLoading} className="bg-[#EAB308] text-white font-bold text-xs uppercase">
                      {stepLoading === "compare" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <BarChart3 size={14} className="mr-2" />} Run Comparison
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                      <div className="border-2 border-zinc-200 bg-white p-3 text-center"><p className="text-[10px] font-bold uppercase text-zinc-500">Total</p><p className="text-2xl font-black">{compSummary.total_activities}</p></div>
                      <div className="border-2 border-red-200 bg-red-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-red-600">Delayed</p><p className="text-2xl font-black text-red-600">{compSummary.delayed_count}</p></div>
                      <div className="border-2 border-emerald-200 bg-emerald-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-emerald-600">On Track</p><p className="text-2xl font-black text-emerald-600">{compSummary.on_track_count}</p></div>
                      <div className="border-2 border-blue-200 bg-blue-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-blue-600">Ahead</p><p className="text-2xl font-black text-blue-600">{compSummary.ahead_count}</p></div>
                      <div className="border-2 border-[#E11D48]/30 bg-orange-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-[#E11D48]">Critical Delayed</p><p className="text-2xl font-black text-[#E11D48]">{compSummary.critical_delayed}</p></div>
                      <div className="border-2 border-zinc-800 bg-zinc-900 p-3 text-center"><p className="text-[10px] font-bold uppercase text-zinc-400">Project Delay</p><p className="text-2xl font-black text-white">{compSummary.project_delay_days}d</p></div>
                    </div>

                    {/* Variance Chart (visual bar) */}
                    <div className="border border-zinc-200 bg-white p-5">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Activity Variance (Days)</h3>
                      <div className="space-y-2">
                        {(comparison.activities || []).filter(a => a.start_variance_days !== 0 || a.finish_variance_days !== 0).slice(0, 15).map((a, i) => {
                          const maxVar = Math.max(...(comparison.activities || []).map(x => Math.abs(x.finish_variance_days || 0)), 1);
                          const pct = Math.min(Math.abs(a.finish_variance_days || 0) / maxVar * 100, 100);
                          return (
                            <div key={i} className="flex items-center gap-3 text-xs">
                              <div className="w-32 truncate font-medium" title={a.name}>{a.name}</div>
                              <div className="flex-1 h-5 bg-zinc-100 relative">
                                <div className={`absolute top-0 h-full ${(a.finish_variance_days || 0) > 0 ? "bg-red-400" : "bg-emerald-400"}`} style={{ width: `${pct}%`, left: (a.finish_variance_days || 0) > 0 ? "50%" : `${50 - pct}%` }} />
                                <div className="absolute top-0 left-1/2 w-px h-full bg-zinc-400" />
                              </div>
                              <div className="w-16 text-right font-mono">{(a.finish_variance_days || 0) > 0 ? "+" : ""}{a.finish_variance_days || 0}d</div>
                              {a.is_critical && <Flag size={10} className="text-red-500 flex-shrink-0" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Comparison Table */}
                    <div className="border border-zinc-200 bg-white overflow-hidden">
                      <div className="bg-zinc-900 text-white p-3 text-xs font-bold uppercase tracking-wider">Baseline vs As-Built Comparison</div>
                      <ScrollArea className="max-h-[400px]">
                        <table className="w-full text-xs">
                          <thead className="bg-zinc-100 sticky top-0">
                            <tr>
                              <th className="p-2 text-left">Activity</th>
                              <th className="p-2 text-center">Planned Start</th>
                              <th className="p-2 text-center">Actual Start</th>
                              <th className="p-2 text-center">Start Var</th>
                              <th className="p-2 text-center">Planned Finish</th>
                              <th className="p-2 text-center">Actual Finish</th>
                              <th className="p-2 text-center">Finish Var</th>
                              <th className="p-2 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(comparison.activities || []).map((a, i) => (
                              <tr key={i} className={`border-b border-zinc-100 ${a.status === "delayed" ? "bg-red-50/40" : ""}`} data-testid={`comp-row-${i}`}>
                                <td className="p-2 font-medium">{a.name} {a.is_critical && <Flag size={10} className="inline text-red-500 ml-1" />}</td>
                                <td className="p-2 text-center">{a.planned_start || "-"}</td>
                                <td className="p-2 text-center">{a.actual_start || "-"}</td>
                                <td className="p-2 text-center font-mono">{getVarIcon(a.start_variance_days)} {a.start_variance_days !== 0 ? `${Math.abs(a.start_variance_days)}d` : "-"}</td>
                                <td className="p-2 text-center">{a.planned_finish || "-"}</td>
                                <td className="p-2 text-center">{a.actual_finish || "-"}</td>
                                <td className="p-2 text-center font-mono">{getVarIcon(a.finish_variance_days)} {a.finish_variance_days !== 0 ? `${Math.abs(a.finish_variance_days)}d` : "-"}</td>
                                <td className="p-2 text-center"><span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase border ${getStatusBadge(a.status)}`}>{a.status}</span></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </ScrollArea>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* TAB 3: Delay Events */}
            <TabsContent value="events">
              <div className="space-y-4">
                {delayEvents.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-zinc-300 bg-zinc-50">
                    <AlertTriangle size={48} className="mx-auto mb-4 opacity-20" />
                    <h3 className="font-bold text-zinc-600 mb-2">No delay events detected</h3>
                    <p className="text-sm text-zinc-400 mb-4">Upload schedule data and run AI-powered delay detection</p>
                    <Button onClick={detectDelays} disabled={!selectedProject.baseline_uploaded || !!stepLoading} className="bg-[#E11D48] text-white font-bold text-xs uppercase">
                      {stepLoading === "detect" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <AlertTriangle size={14} className="mr-2" />} Detect Delays
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-3 text-center"><p className="text-[10px] font-bold uppercase text-zinc-400">Total Events</p><p className="text-2xl font-black">{delaySummary.total_events}</p></div>
                      <div className="border-2 border-red-200 bg-red-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-red-600">Total Delay</p><p className="text-2xl font-black text-red-600">{delaySummary.total_delay_days}d</p></div>
                      <div className="border-2 border-blue-200 bg-blue-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-blue-600">Excusable</p><p className="text-2xl font-black text-blue-600">{delaySummary.excusable_count}</p></div>
                      <div className="border-2 border-amber-200 bg-amber-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-amber-600">Compensable</p><p className="text-2xl font-black text-amber-600">{delaySummary.compensable_count}</p></div>
                      <div className="border-2 border-emerald-200 bg-emerald-50 p-3 text-center"><p className="text-[10px] font-bold uppercase text-emerald-600">EOT Entitled</p><p className="text-2xl font-black text-emerald-600">{delaySummary.recommended_eot_days}d</p></div>
                    </div>

                    {/* Responsibility Breakdown */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="border-2 border-red-200 bg-white p-4 text-center"><p className="text-[10px] font-bold uppercase text-red-600">Employer</p><p className="text-3xl font-black text-red-600">{delaySummary.employer_responsible}</p></div>
                      <div className="border-2 border-blue-200 bg-white p-4 text-center"><p className="text-[10px] font-bold uppercase text-blue-600">Contractor</p><p className="text-3xl font-black text-blue-600">{delaySummary.contractor_responsible}</p></div>
                      <div className="border-2 border-amber-200 bg-white p-4 text-center"><p className="text-[10px] font-bold uppercase text-amber-600">Third Party / FM</p><p className="text-3xl font-black text-amber-600">{delaySummary.third_party_responsible}</p></div>
                    </div>

                    {/* Delay Events Table */}
                    <div className="border border-zinc-200 bg-white overflow-hidden">
                      <div className="bg-zinc-900 text-white p-3 text-xs font-bold uppercase tracking-wider">Classified Delay Events</div>
                      <ScrollArea className="max-h-[500px]">
                        <table className="w-full text-xs">
                          <thead className="bg-zinc-100 sticky top-0">
                            <tr>
                              <th className="p-2 text-left">ID</th>
                              <th className="p-2 text-left">Description</th>
                              <th className="p-2 text-center">Impact</th>
                              <th className="p-2 text-center">Type</th>
                              <th className="p-2 text-center">Compensable</th>
                              <th className="p-2 text-center">Responsibility</th>
                              <th className="p-2 text-center">Critical</th>
                              <th className="p-2 text-left">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {delayEvents.map((ev, i) => (
                              <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`delay-event-${i}`}>
                                <td className="p-2 font-mono font-bold text-[#EAB308]">{ev.delay_id}</td>
                                <td className="p-2 max-w-[200px]"><p className="font-medium truncate" title={ev.description}>{ev.description}</p><p className="text-zinc-400">{ev.affected_activity_name}</p></td>
                                <td className="p-2 text-center font-black text-red-600">{ev.impact_days}d</td>
                                <td className="p-2 text-center"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${getTypeBadge(ev.delay_type)}`}>{ev.delay_type}</span></td>
                                <td className="p-2 text-center"><span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${getCompBadge(ev.compensable)}`}>{ev.compensable}</span></td>
                                <td className="p-2 text-center"><span className={`px-2 py-0.5 text-[10px] font-bold border ${getRespColor(ev.responsibility)}`}>{ev.responsibility}</span></td>
                                <td className="p-2 text-center">{ev.is_critical ? <Flag size={12} className="inline text-red-500" /> : <Minus size={12} className="inline text-zinc-400" />}</td>
                                <td className="p-2 text-emerald-700 font-medium">{ev.suggested_action}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </ScrollArea>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* TAB 4: Evidence Linking */}
            <TabsContent value="evidence">
              <EvidenceLinkingTab
                delayEvents={delayEvents}
                selectedProject={selectedProject}
                availableEvidence={availableEvidence}
                evidenceLoading={evidenceLoading}
                fetchEvidence={fetchEvidence}
                linkEvidence={linkEvidence}
                unlinkEvidence={unlinkEvidence}
                getTypeBadge={getTypeBadge}
                getRespColor={getRespColor}
              />
            </TabsContent>

            {/* TAB 5: Clause Linking */}
            <TabsContent value="clauses">
              <div className="space-y-4">
                {delayEvents.length === 0 ? (
                  <div className="text-center py-12 border-2 border-dashed border-zinc-300 bg-zinc-50">
                    <FileText size={48} className="mx-auto mb-4 opacity-20" />
                    <h3 className="font-bold text-zinc-600 mb-2">Run delay detection first</h3>
                    <p className="text-sm text-zinc-400">Clause linking requires detected delay events</p>
                  </div>
                ) : (
                  <>
                    <div className="border-l-4 border-[#EAB308] bg-blue-50 p-4">
                      <p className="text-sm"><strong>{selectedProject.framework}</strong> contract clauses automatically linked to each delay event based on AI causation analysis.</p>
                    </div>

                    <div className="space-y-3">
                      {delayEvents.map((ev, i) => (
                        <div key={i} className={`border-2 bg-white p-4 ${ev.clause_reference ? "border-[#EAB308]" : "border-zinc-200"}`} data-testid={`clause-link-${i}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-black text-sm text-[#EAB308]">{ev.delay_id}</span>
                                <span className="font-bold text-sm">{ev.description}</span>
                              </div>
                              <p className="text-xs text-zinc-500 mt-1">Activity: {ev.affected_activity_name} ({ev.affected_activity_id})</p>
                            </div>
                            <div className="flex gap-1">
                              <span className={`px-2 py-0.5 text-[10px] font-bold uppercase ${getTypeBadge(ev.delay_type)}`}>{ev.delay_type}</span>
                              <span className={`px-2 py-0.5 text-[10px] font-bold border ${getRespColor(ev.responsibility)}`}>{ev.responsibility}</span>
                            </div>
                          </div>
                          <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200">
                            <div className="flex items-center gap-2 mb-1">
                              <FileText size={14} className="text-[#EAB308]" />
                              <span className="text-xs font-bold uppercase text-zinc-500">Contract Clause Reference</span>
                            </div>
                            {ev.clause_reference ? (
                              <p className="text-sm font-medium text-[#EAB308]">{ev.clause_reference}</p>
                            ) : ev.contract_clause ? (
                              <p className="text-sm font-medium text-zinc-600">{ev.contract_clause}</p>
                            ) : (
                              <p className="text-sm text-zinc-400 italic">No applicable clause identified</p>
                            )}
                            {ev.evidence_reference && (
                              <p className="text-xs text-zinc-500 mt-1">Evidence: {ev.evidence_reference}</p>
                            )}
                          </div>
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-1 border border-emerald-200">{ev.suggested_action}</span>
                            <span className="text-xs text-zinc-500">Impact: {ev.impact_days} days</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* TAB 5: Quantification */}
            <TabsContent value="quantification">
              <div className="space-y-4">
                {!selectedProject.quantification ? (
                  <div className="text-center py-12 border-2 border-dashed border-zinc-300 bg-zinc-50">
                    <Target size={48} className="mx-auto mb-4 opacity-20" />
                    <h3 className="font-bold text-zinc-600 mb-2">Quantification not yet computed</h3>
                    <p className="text-sm text-zinc-400 mb-4">Run delay detection first, then quantify the impact</p>
                    <Button onClick={runQuantification} disabled={delayEvents.length === 0 || !!stepLoading} className="bg-black text-white font-bold text-xs uppercase">
                      {stepLoading === "quantify" ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Target size={14} className="mr-2" />} Quantify Delays
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-4 text-center"><p className="text-[10px] font-bold uppercase text-zinc-400">Total Delay</p><p className="text-3xl font-black">{quantification.total_delay_days}d</p></div>
                      <div className="border-2 border-blue-300 bg-blue-50 p-4 text-center"><p className="text-[10px] font-bold uppercase text-blue-600">Excusable</p><p className="text-3xl font-black text-blue-600">{quantification.excusable_delay_days}d</p></div>
                      <div className="border-2 border-emerald-300 bg-emerald-50 p-4 text-center"><p className="text-[10px] font-bold uppercase text-emerald-600">EOT Entitlement</p><p className="text-3xl font-black text-emerald-600">{quantification.eot_entitlement_days}d</p></div>
                      <div className="border-2 border-[#E11D48]/50 bg-orange-50 p-4 text-center"><p className="text-[10px] font-bold uppercase text-[#E11D48]">Cost Claim</p><p className="text-3xl font-black text-[#E11D48]">${(quantification.total_cost_claim || 0).toLocaleString()}</p></div>
                    </div>

                    {/* Event Cost Breakdown */}
                    <div className="border border-zinc-200 bg-white overflow-hidden">
                      <div className="bg-zinc-900 text-white p-3 text-xs font-bold uppercase tracking-wider">Event-Level Quantification</div>
                      <ScrollArea className="max-h-[400px]">
                        <table className="w-full text-xs">
                          <thead className="bg-zinc-100 sticky top-0">
                            <tr>
                              <th className="p-2 text-left">Delay ID</th>
                              <th className="p-2 text-left">Description</th>
                              <th className="p-2 text-center">Impact (d)</th>
                              <th className="p-2 text-center">Type</th>
                              <th className="p-2 text-center">Responsibility</th>
                              <th className="p-2 text-center">EOT (d)</th>
                              <th className="p-2 text-right">Cost Claim</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(quantification.event_breakdown || []).map((ev, i) => (
                              <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`quant-row-${i}`}>
                                <td className="p-2 font-mono font-bold text-[#EAB308]">{ev.delay_id}</td>
                                <td className="p-2 max-w-[200px] truncate" title={ev.description}>{ev.description}</td>
                                <td className="p-2 text-center font-black">{ev.impact_days}</td>
                                <td className="p-2 text-center"><span className={`px-1 py-0.5 text-[10px] font-bold ${getTypeBadge(ev.delay_type)}`}>{ev.delay_type}</span></td>
                                <td className="p-2 text-center"><span className={`px-1 py-0.5 text-[10px] font-bold border ${getRespColor(ev.responsibility)}`}>{ev.responsibility}</span></td>
                                <td className="p-2 text-center font-black text-emerald-600">{ev.eot_days || 0}</td>
                                <td className="p-2 text-right font-mono">{ev.cost_claim > 0 ? `$${ev.cost_claim.toLocaleString()}` : "-"}</td>
                              </tr>
                            ))}
                            {/* Totals Row */}
                            <tr className="bg-zinc-900 text-white font-bold">
                              <td className="p-2" colSpan={2}>TOTAL</td>
                              <td className="p-2 text-center">{quantification.total_delay_days}</td>
                              <td className="p-2" colSpan={2}></td>
                              <td className="p-2 text-center">{quantification.eot_entitlement_days}</td>
                              <td className="p-2 text-right">${(quantification.total_cost_claim || 0).toLocaleString()}</td>
                            </tr>
                          </tbody>
                        </table>
                      </ScrollArea>
                    </div>

                    <div className="border-2 border-zinc-800 bg-zinc-900 text-white p-5">
                      <h3 className="text-xs font-bold uppercase text-zinc-400 mb-2">Completion Impact</h3>
                      <p className="text-lg font-bold">{quantification.project_completion_impact}</p>
                      <p className="text-xs text-zinc-400 mt-2">Daily prolongation rate: ${(quantification.daily_prolongation_rate || 0).toLocaleString()}/day</p>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            {/* TAB 6: Claims Report */}
            <TabsContent value="report">
              <div className="space-y-4">
                <div className="border-2 border-zinc-200 bg-white p-6 text-center">
                  <FileText size={48} className="mx-auto mb-4 text-[#EAB308]" />
                  <h3 className="font-bold text-lg mb-2">Generate Delay Claims Report</h3>
                  <p className="text-sm text-zinc-500 max-w-lg mx-auto mb-6">
                    Generates a comprehensive PDF report including Executive Summary, Background, Baseline vs As-Built Summary, 
                    Delay Events with classification, Contract References, and Claim Summary (EOT days & cost).
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 max-w-2xl mx-auto">
                    <div className={`p-3 border-2 text-center ${selectedProject.baseline_uploaded ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}>
                      <CheckCircle size={18} className={`mx-auto mb-1 ${selectedProject.baseline_uploaded ? "text-emerald-600" : "text-zinc-300"}`} />
                      <p className="text-[10px] font-bold uppercase">Schedule</p>
                    </div>
                    <div className={`p-3 border-2 text-center ${selectedProject.comparison ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}>
                      <CheckCircle size={18} className={`mx-auto mb-1 ${selectedProject.comparison ? "text-emerald-600" : "text-zinc-300"}`} />
                      <p className="text-[10px] font-bold uppercase">Comparison</p>
                    </div>
                    <div className={`p-3 border-2 text-center ${delayEvents.length > 0 ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}>
                      <CheckCircle size={18} className={`mx-auto mb-1 ${delayEvents.length > 0 ? "text-emerald-600" : "text-zinc-300"}`} />
                      <p className="text-[10px] font-bold uppercase">Delays</p>
                    </div>
                    <div className={`p-3 border-2 text-center ${selectedProject.quantification ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-zinc-50"}`}>
                      <CheckCircle size={18} className={`mx-auto mb-1 ${selectedProject.quantification ? "text-emerald-600" : "text-zinc-300"}`} />
                      <p className="text-[10px] font-bold uppercase">Quantified</p>
                    </div>
                  </div>

                  <Button onClick={generateReport} disabled={delayEvents.length === 0 || !!stepLoading} className="bg-[#EAB308] text-white px-10 py-3 h-auto font-bold uppercase tracking-wider" data-testid="btn-generate-report">
                    {stepLoading === "report" ? <><Loader2 size={16} className="mr-2 animate-spin" /> Generating Report...</> : <><Download size={16} className="mr-2" /> Generate & Download PDF Report</>}
                  </Button>

                  {delayEvents.length === 0 && (
                    <p className="text-xs text-amber-600 mt-3">Complete delay detection and classification before generating report</p>
                  )}
                </div>

                {/* Report Preview Summary */}
                {delayEvents.length > 0 && (
                  <div className="border border-zinc-200 bg-white p-5">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Report Preview Summary</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><span className="text-zinc-500">Project:</span> <strong>{selectedProject.project_name}</strong></div>
                      <div><span className="text-zinc-500">Framework:</span> <strong>{selectedProject.framework}</strong></div>
                      <div><span className="text-zinc-500">Total Delay Events:</span> <strong>{delaySummary.total_events}</strong></div>
                      <div><span className="text-zinc-500">Total Delay Days:</span> <strong className="text-red-600">{delaySummary.total_delay_days}d</strong></div>
                      <div><span className="text-zinc-500">EOT Entitlement:</span> <strong className="text-emerald-600">{quantification.eot_entitlement_days || delaySummary.recommended_eot_days || 0}d</strong></div>
                      <div><span className="text-zinc-500">Cost Claim:</span> <strong className="text-[#E11D48]">${(quantification.total_cost_claim || 0).toLocaleString()}</strong></div>
                      <div><span className="text-zinc-500">Employer Responsible:</span> <strong>{delaySummary.employer_responsible}</strong></div>
                      <div><span className="text-zinc-500">Contractor Responsible:</span> <strong>{delaySummary.contractor_responsible}</strong></div>
                      <div><span className="text-zinc-500">Linked Evidence:</span> <strong className="text-emerald-600">{(selectedProject.linked_evidence || []).length} items</strong></div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Empty state when no project selected */}
      {!selectedProject && projects.length > 0 && (
        <div className="text-center py-12 border-2 border-dashed border-zinc-300 bg-zinc-50">
          <Clock size={48} className="mx-auto mb-4 opacity-20" />
          <h3 className="font-bold text-zinc-600 mb-2">Select a project to continue</h3>
          <p className="text-sm text-zinc-400">Choose an existing analysis project from the list above or create a new one</p>
        </div>
      )}
    </div>
  );
};

export default DelayAnalysisView;
